import { useMemo, useRef, useState } from 'react';
import { fmtC, cn } from '../../lib/utils';
import { vtLinePayout, vtSplitMissing, vtTotals, vtMissingCount } from '../../lib/virtourQuote';
import { VT_LEGACY_VERSION, VT_PRICE_VERSIONS, vtNormalizeVersion } from '../../data/virtourCatalog';
import { RG_LEGACY_VERSION, RG_PRICE_VERSIONS, rgNormalizeVersion } from '../../data/serviceCatalog';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import { useMobile } from '../../hooks/useMobile';
import { useFocus } from '../../hooks/useFocus';

/**
 * QuoteLineItems — Card-based line item display with inline shoot/delivery dates.
 *
 * Each line is a two-row card:
 *   Row 1: [⋮] [#] Service name/desc ... Qty × Rate ... Amount [↑][↓][✕]
 *   Row 2: 📍 Location | 🎬 Shoot [date][time] | 📦 Delivery [date][time]
 *
 * VirTour mode adds a split % between rate and amount: the rate is VirTour's
 * retail, the amount we quote is our cut of it. A line with no split yet reads
 * amber and earns $0, so an unfilled row is visibly costing money.
 *
 * Manual reordering: native HTML5 drag-and-drop on the grip handle, plus ↑/↓
 * keyboard-friendly buttons. Both dispatch onMoveLine(fromIndex, toIndex).
 */


const inputBase = 'bg-surface border border-border rounded text-text-primary placeholder-text-muted focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition';
// Same field, amber: a VirTour line with no split yet. Swapped whole rather
// than appended — `border-border` outranks `border-amber-500` in the sheet.
const inputWarn = 'bg-surface border border-amber-500 rounded text-amber-400 placeholder-amber-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition';
const mobileLabel = 'block text-2xs uppercase tracking-wide text-text-dim mb-0.5';
// A VirTour line sold off the old price column, in the same red the picker uses
// for that column — a legacy line must never hide among current-priced ones.
const legacyPill = 'inline-flex items-center flex-shrink-0 px-1.5 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-400 text-2xs font-semibold whitespace-nowrap';
const legacyTitle = `Quoted off the old price list (${VT_PRICE_VERSIONS.find((v) => v.id === VT_LEGACY_VERSION)?.sub || 'superseded'})`;
// RE lines sold off the Old Real Estate list get the same marker.
const reLegacyTitle = `Quoted off the old Real Estate price list (${RG_PRICE_VERSIONS.find((v) => v.id === RG_LEGACY_VERSION)?.sub || 'superseded'})`;
// An RE line the price list quotes rather than prices at this size (the 3D
// floor plan at 10,000+ sq ft) with no rate typed yet: $0 lines are left off
// the quote, so it reads amber like a VirTour line with no split.
const reRateMissing = (line) => line.quoted && !(parseFloat(line.rate) > 0);
const reRateMissingTitle = 'Quoted at this size: type a rate, or this line is left off the quote';

export default function QuoteLineItems({
  lines = [],
  mode = 're',
  onUpdateLine,
  onRemoveLine,
  onAddLine,
  onUpdateSchedule,
  onAddScheduleEntry,
  onRemoveScheduleEntry,
  onResetLineRate,
  onMoveLine,
}) {
  const { isMobile } = useMobile();
  const isVirtour = mode === 'virtour';
  const { isHidden } = useFocus();
  // Focus hides '+ Add Day' only once a line has a day; with none it adds the first.
  const addDayHidden = isHidden('quote.line.addDay');

  const subtotal = useMemo(() => {
    return lines.reduce((sum, line) => {
      return sum + (parseFloat(line.qty) || 0) * (parseFloat(line.rate) || 0);
    }, 0);
  }, [lines]);

  const vtSummary = useMemo(
    () => (isVirtour ? vtTotals(lines) : null),
    [isVirtour, lines]
  );
  const vtMissing = useMemo(
    () => (isVirtour ? vtMissingCount(lines) : 0),
    [isVirtour, lines]
  );
  const reMissing = useMemo(
    () => (isVirtour ? 0 : lines.filter(reRateMissing).length),
    [isVirtour, lines]
  );

  const handleField = (lineId, field, value) => {
    onUpdateLine(lineId, { [field]: value });
  };

  // Split % edits: empty clears back to "not entered yet" (null), anything else
  // is clamped into 0-100 so a typo can't invent money.
  const handleSplit = (lineId, raw) => {
    if (raw === '') {
      onUpdateLine(lineId, { splitPct: null });
      return;
    }
    const pct = parseFloat(raw);
    onUpdateLine(lineId, { splitPct: Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : null });
  };

  // Native HTML5 drag-and-drop reordering. We track the dragged index in a
  // ref (avoids re-renders during drag) and a state for the hover-target so
  // we can paint a drop indicator.
  const dragFromRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const handleDragStart = (idx) => (e) => {
    dragFromRef.current = idx;
    // Required for Firefox to actually start dragging.
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* noop */ }
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (idx) => (e) => {
    if (dragFromRef.current == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragFromRef.current;
    dragFromRef.current = null;
    setDragOverIdx(null);
    if (from == null || from === idx) return;
    onMoveLine?.(from, idx);
  };
  const handleDragEnd = () => {
    dragFromRef.current = null;
    setDragOverIdx(null);
  };

  return (
    <div className="w-full space-y-3">
      {/* Empty state */}
      {lines.length === 0 && (
        <div className="px-4 py-8 text-center text-text-dim border border-dashed border-border rounded-lg">
          No line items yet
          {mode === 'custom' && ' — click "+ Add Line" below to start'}
        </div>
      )}

      {/* Line item cards */}
      {lines.map((line, idx) => {
        const isAddon = line.source === 're-addon';
        const isReadOnly = mode === 're' && line.source !== 'custom';
        const amount = (parseFloat(line.qty) || 0) * (parseFloat(line.rate) || 0);
        const isDragOver = dragOverIdx === idx;
        const splitMissing = isVirtour && amount > 0 && vtSplitMissing(line);
        const payout = isVirtour ? vtLinePayout(line) : 0;
        // VirTour and RE lines. A line with no recorded version was quoted before
        // the reprice, so it is legacy by definition — the normalizers say so.
        const isReLine = line.source === 're' || line.source === 're-addon';
        const isLegacyPrice = (line.source === 'virtour'
          && vtNormalizeVersion(line.priceVersion) === VT_LEGACY_VERSION)
          || (isReLine && rgNormalizeVersion(line.priceVersion) === RG_LEGACY_VERSION);
        const legacyPillTitle = isReLine ? reLegacyTitle : legacyTitle;
        const rateMissing = isReLine && reRateMissing(line);

        // ── Mobile: stacked card with labeled full-width fields. Reorder via
        // ↑/↓ buttons only (HTML5 drag doesn't fire on touch).
        if (isMobile) {
          const durText = line.dur > 0
            ? (line.dur >= 60 ? `${Math.floor(line.dur / 60)}h${line.dur % 60 > 0 ? ` ${line.dur % 60}m` : ''}` : `${line.dur}m`)
            : '';
          return (
            <div
              key={line.id}
              className={cn(
                'border border-border rounded-lg overflow-hidden',
                isAddon && 'ml-3 opacity-80'
              )}
            >
              {/* ── Header: number + name + actions ── */}
              <div className="flex items-start gap-2 px-3 pt-2.5 pb-1.5 bg-surface">
                <span className="text-xs text-text-dim pt-1.5 flex-shrink-0">{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  {isReadOnly ? (
                    <>
                      <div className="text-sm font-medium text-text-primary leading-snug pt-1">
                        {isAddon && '+ '}{line.desc}
                      </div>
                      {(line.detail || durText) && (
                        <div className="text-xs text-text-dim mt-0.5">
                          {line.detail}{line.detail && durText && ' · '}{durText}
                        </div>
                      )}
                    </>
                  ) : (
                    <input
                      type="text"
                      value={line.desc || ''}
                      onChange={(e) => handleField(line.id, 'desc', e.target.value)}
                      placeholder="Service description"
                      className={`w-full px-2 py-1.5 text-sm font-medium ${inputBase}`}
                    />
                  )}
                  {isLegacyPrice && (
                    <span className={`${legacyPill} mt-1`} title={legacyPillTitle}>Old pricing</span>
                  )}
                </div>
                <div className="flex items-center flex-shrink-0 -mr-2">
                  <button
                    onClick={() => idx > 0 && onMoveLine?.(idx, idx - 1)}
                    disabled={idx === 0}
                    className="p-2 text-text-dim hover:text-accent disabled:opacity-30 rounded transition"
                    title="Move up"
                    aria-label="Move up"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => idx < lines.length - 1 && onMoveLine?.(idx, idx + 1)}
                    disabled={idx === lines.length - 1}
                    className="p-2 text-text-dim hover:text-accent disabled:opacity-30 rounded transition"
                    title="Move down"
                    aria-label="Move down"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onRemoveLine(line.id)}
                    className="p-2 ml-1.5 text-text-dim hover:text-red-400 hover:bg-red-500/10 rounded transition"
                    title="Remove"
                    aria-label="Remove"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* ── Qty × Rate = Amount ── */}
              <div className="flex items-start gap-3 px-3 pb-2.5 bg-surface">
                <div className="w-14 flex-shrink-0">
                  <span className={mobileLabel}>Qty</span>
                  {isReadOnly ? (
                    <div className="py-1.5 text-sm text-text-muted">{line.qty || 1}</div>
                  ) : (
                    <input
                      type="number"
                      value={line.qty || ''}
                      onChange={(e) => handleField(line.id, 'qty', e.target.value)}
                      aria-label="Quantity"
                      min="1"
                      step="1"
                      className={`w-full px-1.5 py-1.5 text-sm text-center ${inputBase}`}
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className={mobileLabel}>Rate</span>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-text-dim text-xs">$</span>
                    <input
                      type="number"
                      value={line.rate || ''}
                      onChange={(e) => handleField(line.id, 'rate', e.target.value)}
                      aria-label="Rate"
                      placeholder={rateMissing ? 'Quote' : undefined}
                      title={rateMissing ? reRateMissingTitle : undefined}
                      min="0"
                      step="0.01"
                      className={`w-full pl-5 pr-2 py-1.5 text-sm text-right ${rateMissing ? inputWarn : inputBase}`}
                    />
                  </div>
                  {line.rateOverride && (
                    <button
                      onClick={() => onResetLineRate?.(line.id)}
                      className="mt-0.5 text-xs text-text-dim hover:text-accent transition py-1.5 pr-2"
                      title="Reset to catalog price"
                    >
                      <s>{fmtC(line.originalRate)}</s> · Reset
                    </button>
                  )}
                </div>
                {isVirtour && (
                  <div className="w-16 flex-shrink-0">
                    <span className={mobileLabel}>Split</span>
                    <div className="relative">
                      <input
                        type="number"
                        value={line.splitPct ?? ''}
                        onChange={(e) => handleSplit(line.id, e.target.value)}
                        aria-label="Split percent"
                        placeholder="—"
                        min="0"
                        max="100"
                        step="1"
                        className={`w-full pl-1.5 pr-5 py-1.5 text-sm text-right ${splitMissing ? inputWarn : inputBase}`}
                      />
                      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-dim text-xs pointer-events-none">%</span>
                    </div>
                  </div>
                )}
                <div className="flex-shrink-0 text-right">
                  <span className={mobileLabel}>{isVirtour ? 'Payout' : 'Amount'}</span>
                  {isVirtour ? (
                    <div className="py-1.5 leading-none">
                      <div className={`text-base font-semibold ${splitMissing ? 'text-amber-400' : 'text-accent'}`}>
                        {fmtC(payout)}
                      </div>
                      <div className="text-2xs text-text-dim mt-1">of {fmtC(amount)}</div>
                    </div>
                  ) : (
                    <div className="py-1.5 text-base font-semibold text-accent leading-none">
                      {fmtC(amount)}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Schedule + Delivery ── */}
              <div className="px-3 py-2.5 bg-surface-2 border-t border-border space-y-2.5">
                {(line.schedule || []).map((entry, si) => (
                  <div key={si} className="space-y-1.5">
                    {(line.schedule || []).length > 1 && (
                      <div className="flex items-center justify-between">
                        <span className="text-2xs font-semibold uppercase tracking-wide text-text-dim">
                          Day {si + 1}
                        </span>
                        <button
                          onClick={() => onRemoveScheduleEntry?.(line.id, si)}
                          className="text-xs text-text-dim hover:text-red-400 transition px-2 py-1.5 -my-1.5 -mr-2"
                          title="Remove this day"
                        >
                          ✕ Remove
                        </button>
                      </div>
                    )}
                    <div>
                      <span className={mobileLabel}>Location</span>
                      <AddressAutocomplete
                        value={entry.location || ''}
                        onChange={(v) => onUpdateSchedule?.(line.id, si, { location: v })}
                        placeholder="Location"
                        className={`w-full px-1.5 py-1.5 text-sm ${inputBase}`}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className={mobileLabel}>Shoot date</span>
                        <input
                          type="date"
                          value={entry.date || ''}
                          onChange={(e) => onUpdateSchedule?.(line.id, si, { date: e.target.value })}
                          aria-label="Shoot date"
                          className={`w-full px-1.5 py-1.5 text-sm ${inputBase}`}
                        />
                      </div>
                      <div>
                        <span className={mobileLabel}>Start time</span>
                        <input
                          type="time"
                          value={entry.time || ''}
                          onChange={(e) => onUpdateSchedule?.(line.id, si, { time: e.target.value })}
                          aria-label="Start time"
                          className={`w-full px-1.5 py-1.5 text-sm ${inputBase}`}
                        />
                      </div>
                      {entry.endDate && (
                        <div>
                          <span className={`${mobileLabel} flex items-center justify-between`}>
                            End date
                            <button
                              onClick={() => onUpdateSchedule?.(line.id, si, { endDate: '' })}
                              className="text-xs text-text-dim hover:text-red-400 normal-case px-2.5 py-1.5 -my-2 -mr-2"
                              title="Remove end date"
                              aria-label="Remove end date"
                            >✕</button>
                          </span>
                          <input
                            type="date"
                            value={entry.endDate || ''}
                            min={entry.date || ''}
                            onChange={(e) => onUpdateSchedule?.(line.id, si, { endDate: e.target.value })}
                            aria-label="End date"
                            className={`w-full px-1.5 py-1.5 text-sm ${inputBase}`}
                          />
                        </div>
                      )}
                      {entry.time && (
                        <div className={cn(!entry.endDate && 'col-start-2')}>
                          <span className={mobileLabel}>End time</span>
                          <input
                            type="time"
                            value={entry.endTime || ''}
                            onChange={(e) => onUpdateSchedule?.(line.id, si, { endTime: e.target.value })}
                            aria-label="End time"
                            className={`w-full px-1.5 py-1.5 text-sm ${inputBase} ${line.source === 're' ? 'opacity-60' : ''}`}
                            title={line.source === 're' ? 'Estimated from duration' : 'End time'}
                          />
                        </div>
                      )}
                    </div>
                    {(!entry.endDate || (si === (line.schedule || []).length - 1 && !addDayHidden)) && (
                      <div className="flex items-center gap-2">
                        {!entry.endDate && (
                          <button
                            onClick={() => onUpdateSchedule?.(line.id, si, { endDate: entry.date || '' })}
                            className="text-xs text-accent/70 hover:text-accent px-2.5 py-1.5 border border-dashed border-accent/30 rounded hover:border-accent/50 transition"
                            title="Add end date for multi-day shoot"
                          >Multi-day</button>
                        )}
                        {si === (line.schedule || []).length - 1 && !addDayHidden && (
                          <button
                            data-focus="quote.line.addDay"
                            onClick={() => onAddScheduleEntry?.(line.id)}
                            className="text-xs text-accent/70 hover:text-accent px-2.5 py-1.5 border border-dashed border-accent/30 rounded hover:border-accent/50 transition"
                          >+ Add Day</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {(line.schedule || []).length === 0 && (
                  <button
                    onClick={() => onAddScheduleEntry?.(line.id)}
                    className="text-xs text-accent/70 hover:text-accent px-2.5 py-1.5 border border-dashed border-accent/30 rounded hover:border-accent/50 transition"
                  >+ Add Day</button>
                )}

                <div className="pt-2 border-t border-border grid grid-cols-2 gap-2">
                  <div>
                    <span className={mobileLabel}>Delivery date</span>
                    <input
                      type="date"
                      value={line.deliveryDate || ''}
                      onChange={(e) => handleField(line.id, 'deliveryDate', e.target.value)}
                      aria-label="Delivery date"
                      className={`w-full px-1.5 py-1.5 text-sm ${inputBase}`}
                    />
                  </div>
                  <div>
                    <span className={mobileLabel}>Time · blank = EOD</span>
                    <input
                      type="time"
                      value={line.deliveryTime === 'EOD' ? '' : (line.deliveryTime || '')}
                      onChange={(e) => handleField(line.id, 'deliveryTime', e.target.value || 'EOD')}
                      aria-label="Delivery time"
                      className={`w-full px-1.5 py-1.5 text-sm ${inputBase}`}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div
            key={line.id}
            onDragOver={handleDragOver(idx)}
            onDrop={handleDrop(idx)}
            onDragEnd={handleDragEnd}
            className={cn(
              'border border-border rounded-lg overflow-hidden transition',
              isAddon && 'ml-6 opacity-80',
              isDragOver && 'border-accent ring-1 ring-accent'
            )}
          >
            {/* ── Row 1: Service + Pricing ── */}
            <div className="flex flex-wrap md:flex-nowrap items-center gap-2 px-3 py-2.5 bg-surface">
              {/* Drag handle */}
              <span
                draggable
                onDragStart={handleDragStart(idx)}
                title="Drag to reorder"
                className="flex-shrink-0 cursor-grab active:cursor-grabbing text-text-dim hover:text-accent select-none px-0.5"
                aria-label="Drag to reorder"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 4a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2zm6-10a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2zm0 5a1 1 0 100 2 1 1 0 000-2z" />
                </svg>
              </span>

              {/* Line number */}
              <span className="w-5 text-center text-xs text-text-dim flex-shrink-0">
                {idx + 1}
              </span>

              {/* Service name / description */}
              {isReadOnly ? (
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-text-primary">
                    {isAddon && '+ '}{line.desc}
                  </span>
                  {line.detail && (
                    <span className="text-xs text-text-dim ml-2">{line.detail}</span>
                  )}
                  {line.dur > 0 && (
                    <span className="text-xs text-text-dim ml-2">
                      · {line.dur >= 60 ? `${Math.floor(line.dur / 60)}h${line.dur % 60 > 0 ? ` ${line.dur % 60}m` : ''}` : `${line.dur}m`}
                    </span>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  value={line.desc || ''}
                  onChange={(e) => handleField(line.id, 'desc', e.target.value)}
                  placeholder="Service description"
                  className={`flex-1 min-w-0 px-2 py-1 text-sm font-medium ${inputBase}`}
                />
              )}

              {/* Old-pricing marker — VirTour lines quoted off the legacy column,
                  RE lines on a quote priced off the Old list */}
              {isLegacyPrice && (
                <span className={legacyPill} title={legacyPillTitle}>Old pricing</span>
              )}

              {/* Qty */}
              {isReadOnly ? (
                <span className="text-xs text-text-muted flex-shrink-0 whitespace-nowrap">
                  {line.qty || 1} ×
                </span>
              ) : (
                <input
                  type="number"
                  value={line.qty || ''}
                  onChange={(e) => handleField(line.id, 'qty', e.target.value)}
                  min="1"
                  step="1"
                  className={`w-14 px-1.5 py-1 text-sm text-center ${inputBase}`}
                />
              )}

              {/* Rate — always editable (RE lines can override catalog price) */}
              <div className="relative flex-shrink-0 flex items-center gap-1">
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-text-dim text-xs">$</span>
                  <input
                    type="number"
                    value={line.rate || ''}
                    onChange={(e) => handleField(line.id, 'rate', e.target.value)}
                    placeholder={rateMissing ? 'Quote' : undefined}
                    title={rateMissing ? reRateMissingTitle : undefined}
                    min="0"
                    step="0.01"
                    className={`w-24 pl-5 pr-2 py-1 text-sm text-right ${rateMissing ? inputWarn : inputBase}`}
                  />
                </div>
                {line.rateOverride && (
                  <>
                    <span className="text-[10px] text-text-dim line-through whitespace-nowrap" title="Catalog price">
                      {fmtC(line.originalRate)}
                    </span>
                    <button
                      onClick={() => onResetLineRate?.(line.id)}
                      className="p-0.5 text-text-dim hover:text-accent rounded transition"
                      title="Reset to catalog price"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4" />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Split % — VirTour only: our cut of VirTour's retail rate */}
              {isVirtour && (
                <div className="relative flex-shrink-0">
                  <input
                    type="number"
                    value={line.splitPct ?? ''}
                    onChange={(e) => handleSplit(line.id, e.target.value)}
                    aria-label="Split percent"
                    title="Roto Flight's share of this line"
                    placeholder="—"
                    min="0"
                    max="100"
                    step="1"
                    className={`w-16 pl-1.5 pr-5 py-1 text-sm text-right ${splitMissing ? inputWarn : inputBase}`}
                  />
                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-dim text-xs pointer-events-none">%</span>
                </div>
              )}

              {/* Amount — VirTour quotes the payout, with the retail beneath it */}
              {isVirtour ? (
                <span className="w-24 text-right flex-shrink-0 leading-tight">
                  <span className={`block text-sm font-semibold ${splitMissing ? 'text-amber-400' : 'text-accent'}`}>
                    {fmtC(payout)}
                  </span>
                  <span className="block text-2xs text-text-dim">of {fmtC(amount)}</span>
                </span>
              ) : (
                <span className="w-20 text-right text-sm font-semibold text-accent flex-shrink-0">
                  {fmtC(amount)}
                </span>
              )}

              {/* Move up */}
              <button
                onClick={() => idx > 0 && onMoveLine?.(idx, idx - 1)}
                disabled={idx === 0}
                className="p-1 text-text-dim hover:text-accent disabled:opacity-30 disabled:hover:text-text-dim disabled:cursor-not-allowed rounded transition flex-shrink-0"
                title="Move up"
                aria-label="Move up"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>

              {/* Move down */}
              <button
                onClick={() => idx < lines.length - 1 && onMoveLine?.(idx, idx + 1)}
                disabled={idx === lines.length - 1}
                className="p-1 text-text-dim hover:text-accent disabled:opacity-30 disabled:hover:text-text-dim disabled:cursor-not-allowed rounded transition flex-shrink-0"
                title="Move down"
                aria-label="Move down"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Remove */}
              <button
                onClick={() => onRemoveLine(line.id)}
                className="p-1 text-text-dim hover:text-red-400 hover:bg-red-500/10 rounded transition flex-shrink-0 opacity-50 hover:opacity-100"
                title="Remove"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* ── Row 2: Schedule entries + Delivery ── */}
            <div className="px-3 py-2 bg-surface-2 border-t border-border text-xs space-y-1.5">
              {/* Schedule entries */}
              {(line.schedule || []).map((entry, si) => (
                <div key={si} className="flex items-center gap-2 flex-wrap">
                  {/* Location */}
                  <div className="flex items-center gap-1 flex-1 min-w-[120px]">
                    <span className="flex-shrink-0" title="Location">📍</span>
                    <AddressAutocomplete
                      value={entry.location || ''}
                      onChange={(v) => onUpdateSchedule?.(line.id, si, { location: v })}
                      placeholder="Location"
                      className={`flex-1 min-w-0 px-2 py-1 text-xs ${inputBase}`}
                    />
                  </div>

                  {/* Shoot date */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span title="Shoot">🎬</span>
                    <input
                      type="date"
                      value={entry.date || ''}
                      onChange={(e) => onUpdateSchedule?.(line.id, si, { date: e.target.value })}
                      className={`w-[130px] px-1.5 py-1 text-xs ${inputBase}`}
                    />
                  </div>

                  {/* Multi-day toggle + end date */}
                  {entry.endDate ? (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className="text-text-dim">→</span>
                      <input
                        type="date"
                        value={entry.endDate || ''}
                        min={entry.date || ''}
                        onChange={(e) => onUpdateSchedule?.(line.id, si, { endDate: e.target.value })}
                        className={`w-[130px] px-1.5 py-1 text-xs ${inputBase}`}
                      />
                      <button
                        onClick={() => onUpdateSchedule?.(line.id, si, { endDate: '' })}
                        className="text-text-dim hover:text-red-400 text-xs px-1"
                        title="Remove end date"
                      >✕</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => onUpdateSchedule?.(line.id, si, { endDate: entry.date || '' })}
                      className="text-2xs text-accent/70 hover:text-accent px-1.5 py-0.5 border border-dashed border-accent/30 rounded hover:border-accent/50 transition flex-shrink-0"
                      title="Add end date for multi-day shoot"
                    >Multi-day</button>
                  )}

                  {/* Shoot time */}
                  <input
                    type="time"
                    value={entry.time || ''}
                    onChange={(e) => onUpdateSchedule?.(line.id, si, { time: e.target.value })}
                    className={`w-[100px] px-1.5 py-1 text-xs flex-shrink-0 ${inputBase}`}
                  />

                  {/* End time */}
                  {entry.time && (
                    <>
                      <span className="text-text-dim flex-shrink-0">→</span>
                      <input
                        type="time"
                        value={entry.endTime || ''}
                        onChange={(e) => onUpdateSchedule?.(line.id, si, { endTime: e.target.value })}
                        className={`w-[100px] px-1.5 py-1 text-xs flex-shrink-0 ${inputBase} ${line.source === 're' ? 'opacity-60' : ''}`}
                        title={line.source === 're' ? 'Estimated from duration' : 'End time'}
                      />
                    </>
                  )}

                  {/* Remove entry (only if >1) */}
                  {(line.schedule || []).length > 1 && (
                    <button
                      onClick={() => onRemoveScheduleEntry?.(line.id, si)}
                      className="p-0.5 text-text-dim hover:text-red-400 rounded transition flex-shrink-0"
                      title="Remove this day"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}

              {/* Add Day button */}
              <div className="flex flex-wrap items-center gap-3">
                {!(addDayHidden && (line.schedule || []).length > 0) && (
                <button
                  data-focus="quote.line.addDay"
                  onClick={() => onAddScheduleEntry?.(line.id)}
                  className="text-2xs text-accent/70 hover:text-accent px-1.5 py-0.5 border border-dashed border-accent/30 rounded hover:border-accent/50 transition"
                >+ Add Day</button>
                )}

                {/* Delivery date + time (line level) */}
                <div className="flex flex-wrap items-center gap-1.5 flex-shrink-0 ml-auto">
                  <span title="Delivery">📦</span>
                  <span className="text-text-dim font-medium">Delivery</span>
                  <input
                    type="date"
                    value={line.deliveryDate || ''}
                    onChange={(e) => handleField(line.id, 'deliveryDate', e.target.value)}
                    className={`w-[130px] px-1.5 py-1 text-xs ${inputBase}`}
                  />
                  <input
                    type="time"
                    value={line.deliveryTime === 'EOD' ? '' : (line.deliveryTime || '')}
                    onChange={(e) => handleField(line.id, 'deliveryTime', e.target.value || 'EOD')}
                    placeholder="EOD"
                    className={`w-[100px] px-1.5 py-1 text-xs ${inputBase}`}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Subtotal — VirTour reports retail, blended split and the payout we quote */}
      {lines.length > 0 && (
        isVirtour ? (
          <div className="flex flex-col items-end gap-1 pr-3 py-2">
            <div className="flex items-baseline">
              <span className="text-xs text-text-dim mr-4">VirTour retail</span>
              <span className="text-sm text-text-muted w-28 text-right">{fmtC(vtSummary.retailTotal)}</span>
            </div>
            <div className="flex items-baseline">
              <span className="text-sm text-text-dim mr-4">
                Payout{vtSummary.retailTotal > 0 ? ` · ${vtSummary.blendedPct}% blended` : ''}
              </span>
              <span className="text-lg font-semibold text-accent w-28 text-right">{fmtC(vtSummary.payoutTotal)}</span>
            </div>
            {vtMissing > 0 && (
              <span className="text-2xs text-amber-400">
                {vtMissing} line{vtMissing === 1 ? '' : 's'} with no split — counted as $0
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-end gap-1 pr-3 py-2">
            <div className="flex items-baseline">
              <span className="text-sm text-text-dim mr-4">Subtotal</span>
              <span className="text-lg font-semibold text-accent">{fmtC(subtotal)}</span>
            </div>
            {reMissing > 0 && (
              <span className="text-2xs text-amber-400 text-right">
                {reMissing} line{reMissing === 1 ? '' : 's'} quoted at this size: type a rate, or {reMissing === 1 ? 'it is' : 'they are'} left off the quote
              </span>
            )}
          </div>
        )
      )}

      {/* Add Line */}
      <button
        onClick={onAddLine}
        className="w-full px-4 py-3 flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg hover:border-accent hover:bg-surface-2 transition text-text-primary font-medium"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        {mode === 're' ? '+ Add Custom Line' : 'Add Line Item'}
      </button>
    </div>
  );
}
